/**
 * PATH: packages/ui/src/components/Dialog.tsx
 * FILE_ID: UI-COMPONENTS-DIALOG-001
 * PURPOSE: Modal dialog that fills the screen below the md breakpoint.
 */
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/utils'

export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  className,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: ReactNode
  className?: string
}) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex bg-foreground/40 max-md:items-stretch md:items-center md:justify-center md:p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'flex w-full min-h-0 flex-col overflow-hidden bg-background',
          'max-md:h-dvh max-md:max-w-none max-md:rounded-none',
          'md:max-h-[85vh] md:max-w-3xl md:rounded-xl md:border md:border-border',
          className,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          <button type="button" className="text-sm text-muted-foreground" onClick={() => onOpenChange(false)}>
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
